import { HttpTransportType, LogLevel } from '@microsoft/signalr';

import { SignalRConfig } from '../services/SignalRService';
import authServices from '@/auth/main/DependencyInjection';

export interface SignalREnvironmentConfig {
  development: SignalRConfig;
  qa: SignalRConfig;
  production: SignalRConfig;
}

/**
 * Get authentication token from cookies using the application's token service
 */
const getAuthToken = (): string => {
  const { tokenService } = authServices;
  const token = tokenService.getToken();
  if (!token) {
    throw new Error('No authentication token available');
  }
  return token;
};

/**
 * Simplified SignalR configuration for different environments
 */
export const signalRConfig: SignalREnvironmentConfig = {
  development: {
    url: import.meta.env.VITE_SIGNALR_URL,
    logLevel: LogLevel.Debug,
    transport: HttpTransportType.WebSockets,
    headers: {},
    withCredentials: true,
    accessTokenFactory: getAuthToken
  },
  qa: {
    url: import.meta.env.VITE_SIGNALR_URL,
    logLevel: LogLevel.Information,
    transport: HttpTransportType.WebSockets,
    headers: {},
    withCredentials: true,
    accessTokenFactory: getAuthToken
  },
  production: {
    url: import.meta.env.VITE_SIGNALR_URL,
    logLevel: LogLevel.Warning,
    transport: HttpTransportType.WebSockets,
    headers: {},
    withCredentials: true,
    accessTokenFactory: getAuthToken
  }
};

/**
 * Get SignalR configuration for current environment
 */
export const getSignalRConfig = (): SignalRConfig => {
  const environment = import.meta.env.MODE || 'development';

  if (!import.meta.env.VITE_SIGNALR_URL) {
    throw new Error('VITE_SIGNALR_URL environment variable is required');
  }

  switch (environment) {
    case 'production':
      return signalRConfig.production;
    case 'qa':
    case 'staging':
      return signalRConfig.qa;
    case 'development':
    default:
      return signalRConfig.development;
  }
};

/**
 * Single SignalR hub name for the entire application
 */
export const SIGNALR_HUB = 'clienthub' as const;

/**
 * SignalR method constants
 */
export const SIGNALR_METHODS = {
  FILE_UPLOAD: '[FileUpload]',
  REPORT_COUNT_VALIDATION: '[ReportCountValidation]'
} as const;
